import { get, post, postMultipart } from '@api/api';
import API_ROUTES from '@api/routes';
import { useTenants } from '@api/tenants';
import { useQuery } from '@tanstack/react-query';
import { replaceTenantsField } from '@utils/tenants';
import { AxiosResponse } from 'axios';
import { ITenants } from 'types/tenants';
import {
	IBatchAction,
	IInviteUsers,
	InviteFunc,
	IUpdateUserTenants,
	IUser,
	IUsers
} from 'types/users';
import QueryKeys from '.';

const getUsers = (route: string, tenants?: ITenants) => async () => {
	const res = await get<IUsers>(route);
	const users = res.data;
	replaceTenantsField(users?.users, tenants);
	return users;
};

const getUser = async (userId: string) => {
	const data = (await get(
		`${API_ROUTES.v1.users}/${userId}`
	)) as AxiosResponse<IUser>;
	return data;
};

export const useUser = (userId: string) =>
	useQuery([QueryKeys.user, userId], () => getUser(userId), {
		enabled: !!userId
	});

export const inviteUsers: InviteFunc = async (data: IInviteUsers) => {
	const res = await post<IInviteUsers>(API_ROUTES.v1.inviteUsers, data);
	return res.data;
};

export const inviteAdminUsers: InviteFunc = async (data: IInviteUsers) => {
	const res = await post<IInviteUsers>(API_ROUTES.v1.admins.invite, data);
	return res.data;
};

export const editUser = async (user: IUser) => {
	const res = await post<IUser>(API_ROUTES.v1.editUser + user.id, user);
	return res.data;
};

export const useUsers = () => {
	const { data, isLoading } = useTenants();
	return useQuery([QueryKeys.users], getUsers(API_ROUTES.v1.users, data), {
		enabled: !isLoading,
		staleTime: 0
	});
};

export const useDescopeAdminUsers = () => {
	const { data, isLoading } = useTenants();
	return useQuery(
		[QueryKeys.adminUsers],
		getUsers(API_ROUTES.v1.admins.all, data),
		{
			enabled: !isLoading
		}
	);
};

export const deleteUsers = async (data: IBatchAction) => {
	const res = await post<IBatchAction, void>(API_ROUTES.v1.batchDelete, data);
	return res.data;
};

export const deleteAdminUser = async (data: IBatchAction) => {
	await post<IBatchAction, void>(API_ROUTES.v1.admins.delete, data);
};

export const suspendUsers = async (data: IBatchAction) => {
	const res = await post<IBatchAction, void>(API_ROUTES.v1.batchSuspend, data);
	return res.data;
};

export const activateUsers = async (data: IBatchAction) => {
	const res = await post<IBatchAction, void>(API_ROUTES.v1.batchActivate, data);
	return res.data;
};

export const updateUsersTenants = async (data: IUpdateUserTenants) => {
	const res = await post<IUpdateUserTenants, void>(
		API_ROUTES.v1.batchTenants,
		data
	);
	return res.data;
};

export const uploadUsers = async (file: Blob) => {
	await postMultipart(API_ROUTES.v1.uploadUsers, file);
};
